import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-dropdown-select';
import geoService from '../../services/geoService';
import { FaMapMarkerAlt, FaHotel, FaBed } from "react-icons/fa";
import { MdHotel, MdOutlineLocalAirport } from "react-icons/md";
import { IoLocationSharp } from "react-icons/io5";
import { MdOutlineTravelExplore } from "react-icons/md";
import { IoCalendarNumberSharp } from "react-icons/io5";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from 'axios';
import config from '../../config';

const SearchForm = ({ onSubmit }) => {
    const [searchParams] = useSearchParams();
    const counterRef = useRef(null);
    const [countries, setCountries] = useState([]);
    const [personalModal, setPersonalModal] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [isSuggestLoading, setIsSuggestLoading] = useState(false);
    const debounceTimeout = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [selectedCountryName, setSelectedCountryName] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
    
    // Nationality autocomplete states
    const [nationalityInputValue, setNationalityInputValue] = useState('');
    const [nationalitySuggestions, setNationalitySuggestions] = useState([]);
    const [isNationalitySuggestLoading, setIsNationalitySuggestLoading] = useState(false);
    const nationalityDebounceTimeout = useRef(null);

    // Icon helper function
    const getIcon = (type) => {
        if (type === "locality") return <FaMapMarkerAlt />;
        if (type === "hotel") return <FaHotel />;
        if (type === "lodging") return <FaBed />;
        if (type === "airport") return <MdOutlineLocalAirport />;
        return <FaMapMarkerAlt />;
    };

    // Filtre parametreleri için state ekle
    const [filterParams, setFilterParams] = useState({
        minPrice: searchParams.get('minPrice') || '',
        maxPrice: searchParams.get('maxPrice') || '',
        star: searchParams.get('star') || '',
        boards: searchParams.get('boards') || '',
        accTypes: searchParams.get('accTypes') || '',
        sort: searchParams.get('sort') || ''
    });

    const [formParams, setFormParams] = useState({
        locationName: searchParams.get('locationName'),
        checkIn: searchParams.get('checkIn'),
        checkOut: searchParams.get('checkOut'),
        nation: searchParams.get('nation'),
        lat: searchParams.get('lat'),
        lng: searchParams.get('lng'),
        hotelbedsCode: searchParams.get('hotelbedsCode'),
        occupanciesStr: searchParams.get('occupancies'),
        rooms: (() => {
            try {
                const occupanciesStr = searchParams.get('occupancies');
                if (occupanciesStr) {
                    const decodedStr = decodeURIComponent(occupanciesStr);
                    const parsed = JSON.parse(decodedStr);
                    
                    if (Array.isArray(parsed)) {
                        return parsed.map((room, index) => ({
                            id: index + 1,
                            adultCounter: room.adults || 2,
                            childCounter: room.children || 0,
                            childrenAges: room.paxes?.filter(pax => pax.type === "CH").map(child => ({
                                type: "CH",
                                age: child.age
                            })) || []
                        }));
                    }
                }
            } catch (error) {
                console.error('Error parsing occupancies:', error);
            }
            return [{
                id: 1,
                adultCounter: 2,
                childCounter: 0,
                childrenAges: []
            }];
        })()
    });

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (counterRef.current && !counterRef.current.contains(event.target)) {
                setPersonalModal(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 700);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await geoService.getCountries();
                setCountries(data);
                
                // URL'den gelen nation parametresine göre ülke adını bul
                if (formParams.nation) {
                    const country = data.find(c => c.iso2 === formParams.nation);
                    if (country) {
                        setSelectedCountryName(country.name);
                        setNationalityInputValue(country.name);
                    }
                }
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };
        fetchCountries();
    }, []);

    const handleSubmitForm = (e) => {
        e.preventDefault();
        
        localStorage.setItem('start_date', formParams.checkIn);
        localStorage.setItem('end_date', formParams.checkOut);
        
        const occupancies = formParams.rooms.map(room => ({
            rooms: 1,
            adults: room.adultCounter,
            children: room.childCounter,
            paxes: room.childrenAges.map(child => ({
                type: "CH",
                age: child.age === "" ? 0 : Number(child.age)
            })).filter(child => child.age !== "")
        }));

        const params = new URLSearchParams();
        params.set('checkIn', formParams.checkIn);
        params.set('checkOut', formParams.checkOut);
        params.set('nation', formParams.nation);
        params.set('lat', formParams.lat);
        params.set('lng', formParams.lng);
        params.set('locationName', formParams.locationName);
        params.set('occupancies', JSON.stringify(occupancies));
        
        if (formParams.hotelbedsCode) {
            params.set('hotelbedsCode', formParams.hotelbedsCode);
        }

        window.location.href = `?${params.toString()}`;
    };

    const handleSuggestInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setFormParams(prev => ({ ...prev, locationName: value }));

        if (value.length < 3) {
            setSuggestions([]);
            return;
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            fetchSuggestions(value);
        }, 200);
    };

    const updateRoomCount = (counterType) => {
        setFormParams(prevState => {
            if (counterType === "increment") {
                return {
                    ...prevState,
                    rooms: [
                        ...prevState.rooms,
                        {
                            id: prevState.rooms.length + 1,
                            adultCounter: 2,
                            childCounter: 0,
                            childrenAges: [],
                        }
                    ]
                };
            } else if (counterType === "decrement" && prevState.rooms.length > 1) {
                return {
                    ...prevState,
                    rooms: prevState.rooms.slice(0, -1)
                };
            }
            return prevState;
        });
    };

    const updateRoomCounters = (roomId, type, counterType) => {
        setFormParams(prevState => ({
            ...prevState,
            rooms: prevState.rooms.map(room => {
                if (room.id === roomId) {
                    if (type === "adult") {
                        return {
                            ...room,
                            adultCounter:
                                counterType === "increment"
                                    ? room.adultCounter + 1
                                    : Math.max(1, room.adultCounter - 1),
                        };
                    } else if (type === "child") {
                        const newChildCounter =
                            counterType === "increment"
                                ? room.childCounter + 1
                                : Math.max(0, room.childCounter - 1);

                        const validChildCounter = Math.max(0, newChildCounter);

                        // Çocuk sayısı azaldığında yaşları koru, arttığında yeni yaş ekle
                        const updatedChildrenAges = validChildCounter < room.childrenAges.length
                            ? room.childrenAges.slice(0, validChildCounter)
                            : [
                                ...room.childrenAges,
                                ...Array(validChildCounter - room.childrenAges.length).fill({ type: 'CH', age: "" }),
                            ];

                        return {
                            ...room,
                            childCounter: validChildCounter,
                            childrenAges: updatedChildrenAges,
                        };
                    }
                }
                return room;
            })
        }));
    };

    const handleChildAgeChange = (roomId, index, value) => {
        const newRooms = [...formParams.rooms];
        const roomIndex = newRooms.findIndex(room => room.id === roomId);
        if (roomIndex !== -1) {
            newRooms[roomIndex].childrenAges[index].age = value;
            setFormParams({ ...formParams, rooms: newRooms });
        }
    };

    const fetchSuggestions = async (searchText) => {
        try {
            setIsSuggestLoading(true);
            const data = await geoService.getAutoComplete(searchText);
            setSuggestions(data.slice(0, 10));
        } catch (error) {
            console.error("Error fetching suggestions:", error);
            setSuggestions([]);
        } finally {
            setIsSuggestLoading(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        if (suggestion.type === "HOTEL" && suggestion.countryCode) {
            const country = countries.find(c => c.iso2 === suggestion.countryCode);
            if (country) {
                localStorage.setItem('nation', country.iso2);
                setFormParams(prev => ({
                    ...prev,
                    nation: country.iso2
                }));
            }
        }

        setFormParams(prev => ({
            ...prev,
            locationName: suggestion.name,
            lat: suggestion.latitude,
            lng: suggestion.longitude,
            hotelbedsCode: suggestion.hotelbeds_code || null
        }));
        setSuggestions([]);
        setInputValue(suggestion.name);
    };

    // Nationality autocomplete functions
    const fetchNationalitySuggestions = async (searchText) => {
        setIsNationalitySuggestLoading(true);
        try {
            const filtered = countries.filter(country => 
                country.name.toLowerCase().includes(searchText.toLowerCase())
            );
            setNationalitySuggestions(filtered.slice(0, 10));
        } catch (error) {
            console.error("Error filtering nationality suggestions:", error);
            setNationalitySuggestions([]);
        } finally {
            setIsNationalitySuggestLoading(false);
        }
    };

    const handleNationalityInputChange = (e) => {
        const value = e.target.value;
        setNationalityInputValue(value);
        
        if (value.length < 2) {
            setNationalitySuggestions([]);
            return;
        }
        
        if (nationalityDebounceTimeout.current) {
            clearTimeout(nationalityDebounceTimeout.current);
        }
        
        nationalityDebounceTimeout.current = setTimeout(() => {
            fetchNationalitySuggestions(value);
        }, 200);
    };

    const handleNationalitySuggestionClick = (suggestion) => {
        setFormParams(prev => ({
            ...prev,
            nation: suggestion.iso2
        }));
        setNationalityInputValue(suggestion.name);
        localStorage.setItem('nation', suggestion.iso2);
        setNationalitySuggestions([]);
    };

    return (
        <form onSubmit={handleSubmitForm}>
            <div className="d-flex flex-column">
                <div className={`d-flex form-info ${isMobile ? 'mobile' : 'desktop'}`}>
                    {/* Destination & Hotel */}
                    <div className="form-item dropdown" style={{ 
                        position: 'relative',
                        zIndex: 10002
                    }}>
                        <div className="form-container">
                            <div className="form-icon">
                                <MdOutlineTravelExplore />
                            </div>
                            
                            <div className="form-content">
                                <label className="form-label">
                                    Destination & Hotel
                                </label>
                                <input
                                    type="text"
                                    placeholder="Where would you like to go?"
                                    value={formParams.locationName}
                                    onChange={handleSuggestInputChange}
                                    className="form-control"
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        backgroundColor: '#fff',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                    }}
                                />
                                
                                {isSuggestLoading && (
                                    <div className="loading-container" style={{ zIndex: 99999 }}>
                                        <i className="isax isax-loading spin me-2"></i>
                                        Searching...
                                    </div>
                                )}
                                {suggestions.length > 0 && (
                                    <ul className="dropdown-menu-custom" style={{ zIndex: 99999 }}>
                                        {suggestions.map((suggestion, index) => (
                                            <li
                                                key={index}
                                                onClick={() => handleSuggestionClick(suggestion)}
                                                className="dropdown-item-custom"
                                            >
                                                <span style={{ color: '#1e3a8a', fontSize: '16px' }}>
                                                    {getIcon(suggestion.type)}
                                                </span>
                                                {suggestion.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Check In & Check Out */}
                    <div className="form-item" style={{ 
                        minWidth: '320px',
                        position: 'relative',
                        zIndex: 10001
                    }}>
                        <div className="date-range-container">
                            <div className="form-icon">
                                <IoCalendarNumberSharp />
                            </div>
                            
                            <div className="form-content">
                                <label className="form-label">
                                    Check In - Check Out
                                </label>
                                
                                <div>
                                    <DatePicker
                                        onChange={(dates) => {
                                            if (Array.isArray(dates)) {
                                                const [start, end] = dates;
                                                
                                                if (start) {
                                                    const year = start.getFullYear();
                                                    const month = String(start.getMonth() + 1).padStart(2, '0');
                                                    const day = String(start.getDate()).padStart(2, '0');
                                                    const formattedStart = `${year}-${month}-${day}`;
                                                    setFormParams(prev => ({ ...prev, checkIn: formattedStart }));
                                                    localStorage.setItem('start_date', formattedStart);
                                                }
                                                
                                                if (end) {
                                                    const year = end.getFullYear();
                                                    const month = String(end.getMonth() + 1).padStart(2, '0');
                                                    const day = String(end.getDate()).padStart(2, '0');
                                                    const formattedEnd = `${year}-${month}-${day}`;
                                                    setFormParams(prev => ({ ...prev, checkOut: formattedEnd }));
                                                    localStorage.setItem('end_date', formattedEnd);
                                                } else {
                                                    setFormParams(prev => ({ ...prev, checkOut: '' }));
                                                    localStorage.setItem('end_date', '');
                                                }
                                            }
                                        }}
                                        startDate={formParams.checkIn ? new Date(formParams.checkIn) : null}
                                        endDate={formParams.checkOut ? new Date(formParams.checkOut) : null}
                                        selectsRange={true}
                                        inline={false}
                                        isClearable={false}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText={
                                            !formParams.checkIn 
                                                ? "Select dates" 
                                                : !formParams.checkOut 
                                                    ? "Select checkout date" 
                                                    : `${new Date(formParams.checkIn).toLocaleDateString('tr-TR')} - ${new Date(formParams.checkOut).toLocaleDateString('tr-TR')}`
                                        }
                                        minDate={new Date()}
                                        maxDate={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)}
                                        className="form-control"
                                        style={{
                                            border: 'none',
                                            padding: '0',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            backgroundColor: 'transparent',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            color: '#2c3e50',
                                            cursor: 'pointer',
                                            width: '100%'
                                        }}
                                        calendarClassName="custom-datepicker"
                                        popperClassName="custom-datepicker-popper"
                                        wrapperClassName="custom-datepicker-wrapper"
                                        popperProps={{
                                            zIndex: 99999
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Guests */}
                    <div className="form-item dropdown" style={{ 
                        position: 'relative', 
                        zIndex: 10000,
                        width: '300px'
                    }}>
                        <div 
                            className="guests-container"
                            onClick={() => setPersonalModal(!personalModal)}
                        >
                            <div className="form-icon">
                                <i className="isax isax-people" style={{ 
                                    color: 'rgb(30, 58, 138)', 
                                    fontSize: '20px' 
                                }}></i>
                            </div>
                            
                            <div style={{ 
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}>
                                <label className="form-label" style={{
                                    fontWeight: '600',
                                    color: 'rgb(30, 58, 138)',
                                    fontSize: '12px',
                                    textTransform: 'capitalize',
                                    letterSpacing: '0.5px',
                                    margin: 0,
                                    lineHeight: 1
                                }}>
                                    Guests
                                </label>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                    <span style={{ 
                                        fontWeight: '500', 
                                        color: '#2c3e50',
                                        fontSize: '15px'
                                    }}>
                                        {formParams.rooms?.reduce((total, room) => total + room.adultCounter + room.childCounter, 0)} Guests, {formParams.rooms?.length} Room{formParams.rooms?.length > 1 ? 's' : ''}
                                    </span>
                                    <i className="isax isax-arrow-down-1" style={{ 
                                        color: '#64748b', 
                                        fontSize: '16px',
                                        marginLeft: '8px'
                                    }}></i>
                                </div>
                            </div>
                        </div>

                        <div
                            className="guests-dropdown"
                            ref={counterRef}
                            style={{
                                display: personalModal ? "block" : "none",
                                zIndex: 99999
                            }}
                        >
                            {/* Header with rooms counter */}
                            <div className="guests-dropdown-header">
                                <div className="header-title">
                                    <i className="isax isax-home"></i>
                                    Rooms
                                </div>
                                <div className="guests-counter-group">
                                    <button
                                        type="button"
                                        onClick={() => updateRoomCount("decrement")}
                                        className="guests-counter-btn"
                                        disabled={formParams.rooms.length <= 1}
                                    >
                                        −
                                    </button>
                                    <span className="guests-counter-value">
                                        {formParams.rooms.length}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => updateRoomCount("increment")}
                                        className="guests-counter-btn"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            {/* Room details section */}
                            <div className="guests-rooms-section">
                                {formParams.rooms.map((room, roomIndex) => (
                                    <div
                                        key={room.id}
                                        className="guests-room-card"
                                    >
                                        <div className="guests-room-title">
                                            <i className="isax isax-home-2"></i>
                                            Room {roomIndex + 1}
                                        </div>
                                        
                                        {/* Adult counter */}
                                        <div className="guests-counter-row">
                                            <div className="guests-counter-label">
                                                <i className="isax isax-user"></i>
                                                Adults
                                            </div>
                                            <div className="guests-counter-group">
                                                <button
                                                    type="button"
                                                    onClick={() => updateRoomCounters(room.id, "adult", "decrement")}
                                                    className="guests-counter-btn"
                                                    disabled={room.adultCounter <= 1}
                                                >
                                                    −
                                                </button>
                                                <span className="guests-counter-value">
                                                    {room.adultCounter}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => updateRoomCounters(room.id, "adult", "increment")}
                                                    className="guests-counter-btn"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>

                                        {/* Children counter */}
                                        <div className="guests-counter-row">
                                            <div className="guests-counter-label">
                                                <i className="isax isax-user-octagon"></i>
                                                Children
                                            </div>
                                            <div className="guests-counter-group">
                                                <button
                                                    type="button"
                                                    onClick={() => updateRoomCounters(room.id, "child", "decrement")}
                                                    className="guests-counter-btn"
                                                    disabled={room.childCounter <= 0}
                                                >
                                                    −
                                                </button>
                                                <span className="guests-counter-value">
                                                    {room.childCounter}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => updateRoomCounters(room.id, "child", "increment")}
                                                    className="guests-counter-btn"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>

                                        {/* Children ages */}
                                        {room.childCounter > 0 && (
                                            <div className="guests-children-ages">
                                                <div className="ages-title">
                                                    <i className="isax isax-calendar"></i>
                                                    Children Ages
                                                </div>
                                                <div className="guests-children-ages-grid">
                                                    {room.childrenAges.map((child, index) => (
                                                        <div key={index} className="guests-child-age-item">
                                                            <div className="guests-child-age-label">
                                                                Child {index + 1}
                                                            </div>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="17"
                                                                value={child.age}
                                                                onChange={(e) => {
                                                                    const value = Math.min(17, Math.max(0, parseInt(e.target.value) || 0));
                                                                    handleChildAgeChange(room.id, index, value);
                                                                }}
                                                                className="guests-child-age-input"
                                                                placeholder="Age"
                                                            />
                                                            <div className="guests-child-age-suffix">
                                                                years
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                    {/* Nationality */}
                    <div className="form-item dropdown" style={{ 
                        minWidth: '280px',
                        position: 'relative',
                        zIndex: 10003
                    }}>
                        <div className="nationality-container">
                            <div className="form-icon">
                                <i className="isax isax-flag" style={{ 
                                    color: 'rgb(30, 58, 138)', 
                                    fontSize: '20px' 
                                }}></i>
                            </div>
                            
                            <div className="form-content">
                                <label className="form-label">
                                    Nationality
                                </label>
                                <input
                                    type="text"
                                    placeholder="Search nationality"
                                    value={nationalityInputValue}
                                    onChange={handleNationalityInputChange}
                                    className="form-control"
                                    style={{
                                        border: 'none',
                                        padding: '0',
                                        fontSize: '15px',
                                        fontWeight: '500',
                                        backgroundColor: 'transparent',
                                        outline: 'none',
                                        boxShadow: 'none',
                                        color: '#2c3e50'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.color = '#1e3a8a';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.color = '#2c3e50';
                                    }}
                                />
                                
                                {isNationalitySuggestLoading && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: '-16px',
                                        right: '-16px',
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                                        padding: '15px',
                                        textAlign: 'center',
                                        fontSize: '14px',
                                        color: '#1e3a8a',
                                        fontWeight: '500',
                                        zIndex: 99999,
                                        marginTop: '8px'
                                    }}>
                                        <i className="isax isax-loading spin me-2"></i>
                                        Searching...
                                    </div>
                                )}
                                {nationalitySuggestions.length > 0 && (
                                    <ul style={{ 
                                        width: "calc(100% + 32px)",
                                        position: 'absolute',
                                        top: '100%',
                                        left: '-16px',
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                                        border: 'none',
                                        padding: '8px 0',
                                        margin: '8px 0 0 0',
                                        zIndex: 99999,
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        listStyle: 'none'
                                    }} className="dropdown-menu-custom">
                                        {nationalitySuggestions.map((suggestion, index) => (
                                            <li
                                                key={index}
                                                onClick={() => handleNationalitySuggestionClick(suggestion)}
                                                className="dropdown-item-custom"
                                                style={{
                                                    padding: '12px 18px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    borderBottom: index < nationalitySuggestions.length - 1 ? '1px solid #f5f7fa' : 'none',
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    color: '#2c3e50',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#f1f5f9';
                                                    e.target.style.color = '#1e3a8a';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = 'transparent';
                                                    e.target.style.color = '#2c3e50';
                                                }}
                                            >
                                                <img
                                                    src={`https://flagcdn.com/w40/${suggestion.iso2.toLowerCase()}.png`}
                                                    alt={suggestion.name}
                                                    style={{ width: '20px', height: '15px' }}
                                                />
                                                {suggestion.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Submit Button - Desktop'ta aynı satırda, Mobile'da alt satırda */}
                    {!isMobile && (
                        <div className="submit-btn-desktop">
                            <button type="submit" className="search-btn desktop">
                                <i className="isax isax-search-normal"></i>
                            </button>
                        </div>
                    )}
                </div>
                
                {/* Submit Button - Sadece Mobile'da alt satırda */}
                {isMobile && (
                    <div className="submit-btn-mobile">
                        <div className="btn-container">
                            <button type="submit" className="search-btn mobile">
                                <i className="isax isax-search-normal"></i>
                                <span className="ms-2">Search</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </form>
    );
};

export default SearchForm; 